<template>
    <div>
        <Panel :title="$t('Licence Type Summary')">
            <Stack class="licence-summary-section" direction="row" gap="1rem" align-items="end">
                <DatePicker
                    :disabled-date="(val) => val > initialStart.toDate()"
                    :label="$t('Date From')"
                    :value="startDate"
                    :clearable="false"
                    input-id="fromDate"
                    @input="(val) => (startDate = val.local(true))"
                />
                <DatePicker
                    :disabled-date="(val) => val <= startDate.toDate() || val > initialEnd.toDate()"
                    :label="$t('Date To')"
                    :value="endDate"
                    :clearable="false"
                    input-id="toDate"
                    @input="(val) => (endDate = val.local(true))"
                />
                <TextButton :loading="$store.isLoading('licencesummary/concurrent')" @click="loadConcurrent">
                    {{ $t('Run Report') }}
                </TextButton>
                <TextButton :disabled="!canExport">
                    <a
                        v-if="canExport"
                        :href="link"
                        :download="`${$t('Licence Type Summary').replace(/\s/g, '')}_${startDate.format(
                            'YYYY-MM-DD',
                        )}_${endDate.format('YYYY-MM-DD')}.csv`"
                    >
                        {{ $t('Export') }}
                    </a>
                    <span v-else>{{ $t('Export') }}</span>
                </TextButton>
            </Stack>
            <div>
                <div v-if="licences.some((licence) => getChanged(licence.quantityId))" class="licence-summary-section">
                    {{
                        $t(
                            '* Total displayed is the total at the end of the report period, but the total changed within the period',
                        )
                    }}
                </div>
                <table id="licencesummary_table" class="cic-table">
                    <thead>
                        <tr>
                            <th>{{ $t('Licence') }}</th>
                            <th>{{ $t('Max Usage') }}</th>
                            <th>
                                {{ $t('Currently Allocated') }}
                            </th>
                            <th>{{ $t('Total') }}</th>
                        </tr>
                    </thead>
                    <LoadingTable v-if="$store.isLoading('licencesummary/get')" :columns="4" />
                    <tbody v-else>
                        <tr
                            v-for="licence in licences"
                            :style="{ cursor: getAssigned(licence.quantityId) > 0 ? 'pointer' : 'normal' }"
                            @click="openForm(licence)"
                        >
                            <td>{{ licence.name }}</td>
                            <td>
                                <div v-if="!licence.concurrentId">{{ $t('N/A') }}</div>
                                <LoadingBalls v-else-if="$store.isLoading('licencesummary/concurrent')" />
                                <div v-else>{{ getConcurrent(licence.name) }}</div>
                            </td>
                            <td>{{ getAssigned(licence.quantityId).toString() }}</td>
                            <td>
                                <span>{{ getTotal(licence.quantityId).toString() }}</span>
                                <span v-if="getChanged(licence.quantityId)">*</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Panel>

        <LicenceSummaryUsers v-if="item !== null" :title="item.name" :users="users" @blur="item = null" />
    </div>
</template>

<script>
    import moment from 'moment/moment';
    import { LoadingBalls, LoadingTable, Panel, Stack } from '@/components/container';
    import { TextButton } from '@/components/controls';
    import { DatePicker } from '@/components/inputs';
    import { post } from '@/utils/Http';
    import LicenceSummaryUsers from './LicenceSummaryUsers';
    import '../styles/licenceAdmin.css';

    export default {
        name: 'LicenceSummary',
        components: {
            DatePicker,
            LicenceSummaryUsers,
            LoadingBalls,
            LoadingTable,
            Panel,
            Stack,
            TextButton,
        },
        data() {
            return {
                assigned: [],
                concurrent: [],
                endDate: null,
                endUTC: null,
                initialEnd: moment(moment().format('YYYY-MM-DD 00:00:00')).subtract(1, 'd'),
                initialStart: moment(moment().format('YYYY-MM-DD 00:00:00')).subtract(2, 'd'),
                item: null,
                licences: [],
                startDate: null,
                startUTC: null,
                users: [],
            };
        },
        computed: {
            canExport() {
                return (
                    !this.$store.isLoading('licencesummary/get') && !this.$store.isLoading('licencesummary/concurrent')
                );
            },
            link() {
                let csvContent =
                    'data:text/csv;charset=utf-8,' +
                    [[this.$t('Licence'), this.$t('Max Usage'), this.$t('Currently Allocated'), this.$t('Total')]]
                        .concat(
                            this.licences.map(
                                (licence) =>
                                    `${licence.name},${
                                        licence.concurrentId ? this.getConcurrent(licence.name) : this.$t('N/A')
                                    },${this.getAssigned(licence.quantityId).toString()},${this.getTotal(
                                        licence.quantityId,
                                    ).toString()}`,
                            ),
                        )
                        .join('\n');
                return encodeURI(csvContent);
            },
        },
        created() {
            this.$store.darkMode = localStorage.darkMode === 'true';
            this.$store.switchMode = true;
            this.startDate = this.initialStart;
            this.endDate = this.initialEnd;
            this.loadTable();
            this.loadConcurrent();
        },
        methods: {
            getAssigned(licenceId) {
                return this.assigned
                    .filter((assign) => assign.licenceId === licenceId)
                    .reduce((total, assign) => total + assign.assigned.length, 0);
            },
            getChanged(licenceId) {
                return (
                    this.startUTC &&
                    this.assigned.some(
                        (assign) =>
                            assign.quantity &&
                            assign.start &&
                            assign.licenceId === licenceId &&
                            ((assign.start > this.startUTC && assign.start < this.endUTC) ||
                                (assign.end > this.startUTC && assign.end < this.endUTC)),
                    )
                );
            },
            getConcurrent(name) {
                return this.concurrent.find((conc) => conc.name === name)?.maxCount?.toString() ?? '0';
            },
            getTotal(licenceId) {
                return this.startUTC
                    ? this.assigned
                          .filter(
                              (assign) =>
                                  assign.quantity &&
                                  assign.licenceId === licenceId &&
                                  (!assign.start || (assign.start <= this.endUTC && assign.end >= this.endUTC)),
                          )
                          .reduce((total, assign) => total + assign.quantity, 0)
                    : 0;
            },
            loadConcurrent() {
                this.startUTC = this.startDate.utc().format('YYYY-MM-DD HH:mm:ss');
                this.endUTC = this.endDate.utc().format('YYYY-MM-DD HH:mm:ss');
                post('licencesummary/concurrent', { from: this.startUTC, to: this.endUTC }, (response) => {
                    this.concurrent = response.licences;
                });
            },
            loadTable() {
                post('licencesummary/get', {}, (response) => {
                    this.assigned = response.assigned.map((assign) =>
                        assign.start
                            ? {
                                  ...assign,
                                  start: this.parseDate(assign.start),
                                  end: this.parseDate(assign.end),
                              }
                            : assign,
                    );
                    this.licences = response.licences.sort((a, b) => (a.name > b.name ? 1 : -1));
                });
            },

            openForm(licence) {
                const userIds = this.assigned
                    .filter((assign) => assign.licenceId === licence.quantityId)
                    .reduce((all, assign) => all.concat(assign.assigned), []);
                this.users = [];
                if (userIds.length > 0) {
                    this.item = licence;
                    post('licencesummary/users', { licenceId: licence.quantityId, userIds }, (response) => {
                        this.users = response.users.sort((a, b) => a.username > b.username);
                    });
                }
            },

            parseDate(date) {
                return `${date.substring(0, 10)} ${date.substring(11, 19)}`;
            },
        },
    };
</script>
